import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { Field, FieldType } from '../../../../../service-groups/articles/article-details/field/field.model';
import { UtilsService } from '../../../../../core/utils/utils.service';
import { Job, JobPhoto } from '../../job.model';
import { CdkDragDrop, CdkDragEnter } from '@angular/cdk/drag-drop';
import { Attachment } from '../../../../../core/attachment/attachment.model';
import { LoggerService } from '../../../../../core/logger/logger.service';
import { SettingsService } from '../../../../../settings/settings.service';
import { Subscription } from 'rxjs';
import { Project } from '../../../../project.model';
import { ProjectService } from '../../../../project.service';
import moment from 'moment/moment';
import { NotificationService } from '../../../../../core/notification/notification.service';
import { CanSeePhotoDateSetting } from '../../../../../core/shared-models/license-type.model';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from '../../../../../core/storage/storage.service';
import { AttachmentService } from '../../../../../core/attachment/attachment.service';
import { AuthService } from '../../../../../auth/auth.service';

@Component({
    selector: 'app-job-article',
    templateUrl: './job-article.component.html',
    styleUrls: ['./job-article.component.scss'],
})
export class JobArticleComponent implements OnInit, OnDestroy, OnChanges {
    @Input() articleFormGroup: UntypedFormGroup; // the article formgroup
    @Input() job: Job;
    @Input() serviceGroupPhotoCategoriesCount = 0; // used to know how many space we have on desktop views, because service group photo categories shall be displayed beside articles
    @Input() viewMode: 'all' | 'data-only' | 'photos-only' = 'all'; // all if we want to show all infos (default) or photos-only if the component is used on phones in the job-photos segment to only display photos
    @Input() changesAllowed = false;
    @Input() asDialog = false;

    @Input() jobExtensionId: string; // only used for article link

    // only used for public code capture
    @Input() publicView = false;
    @Input() publicPhotosToUpload: Array<{ files: Array<{ id: string; data: File }>; category: string }> = [];
    @Output() notUploadedPhotosChanged = new EventEmitter<{ files: File | File[]; category: string }>();
    @Output() notUploadedPhotosDeleted = new EventEmitter();

    @Output() articleFormGroupChanged = new EventEmitter();
    @Output() jobSaveRequested = new EventEmitter();
    @Output() lockRequested = new EventEmitter<string>();
    @Output() removedJobLinkId = new EventEmitter<string>();
    @Output() addedJobLinkId = new EventEmitter<string>();

    project: Project;

    fieldVisibilityMapping = []; // used to calculate if a field is visible or not

    currentProjectSubscription: Subscription;

    // helper
    possibleFieldTypes = FieldType;
    showPhotoDate = CanSeePhotoDateSetting.ONLY_DATE;
    notificationReminderDate = 0; // used to display reminder notification date info if one is active
    fileTypeLabels: any = {
        image: this.translate.instant('projects.jobs.details.file-types.image'),
        video: this.translate.instant('projects.jobs.details.file-types.video'),
        audio: this.translate.instant('projects.jobs.details.file-types.audio'),
        pdf: this.translate.instant('projects.jobs.details.file-types.pdf'),
    };

    articleSearchBarCurrentValue = '';

    dragTargetAllowed = true;
    altPressed = false;

    @HostListener('window:keydown.alt') onAltDown() {
        this.altPressed = true;
    }

    @HostListener('window:keyup.alt') onAltUp() {
        this.altPressed = false;
    }

    getTranslatedFileTypes(fileTypes: string[]): string {
        return fileTypes.map((type) => this.fileTypeLabels[type] || type).join(', ');
    }

    constructor(
        public utils: UtilsService,
        private notifySrv: NotificationService,
        private logger: LoggerService,
        protected authSrv: AuthService,
        private projectSrv: ProjectService,
        private settingsSrv: SettingsService,
        private translate: TranslateService,
        private storage: StorageService,
        private attachmentSrv: AttachmentService
    ) {}

    ngOnInit() {
        this.currentProjectSubscription = this.projectSrv.currentProject$.subscribe((proj) => {
            this.project = proj;
        });

        // only being admin or when this setting is true will the mobile user see image times
        this.showPhotoDate = this.settingsSrv.getUserRights().canSeePhotoDate;

        this.checkReminderInfo();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.articleFormGroup) {
            // if the input form group changes we calculate the field visibility
            if (this.articleFormGroup != null) {
                this.fieldVisibilityMapping = [];
                this.handleArticleChangeEvents(changes.articleFormGroup.isFirstChange());
            }
        }
    }

    ngOnDestroy() {
        if (this.currentProjectSubscription) {
            this.currentProjectSubscription.unsubscribe();
        }
    }

    checkReminderInfo() {
        if (this.articleFormGroup.get('articleReminderEnabled').value === true) {
            // calculate reminder date
            let calculatedReminderDate = moment()
                .add(
                    this.articleFormGroup.get('articleReminderDuration').value,
                    this.articleFormGroup.get('articleReminderDurationType').value
                )
                .hour(0)
                .minute(0)
                .second(0)
                .millisecond(0);

            if (!isNaN(calculatedReminderDate.valueOf())) {
                this.notificationReminderDate = calculatedReminderDate.valueOf();
            }
        }
    }

    /**
     * Triggered when changes of fields occur and we need to handle certain change events
     * @param isFirstChange
     */
    handleArticleChangeEvents(isFirstChange: boolean) {
        this.calculateFieldVisibility();
        if (!isFirstChange) {
            this.articleFormGroupChanged.emit();
        }
    }

    /**
     * Calculates the field visibility for our current article
     */
    calculateFieldVisibility() {
        for (let fieldFormGroup of this.getArticleFieldsFormArray().controls) {
            let fieldData = fieldFormGroup.value as Field;
            let previousVisibility = this.fieldVisibilityMapping[fieldData._id]; // save previous visibility so we check if it has changed

            // update visibility
            this.fieldVisibilityMapping[fieldData._id] = this.utils.checkFieldVisibilityCondition(
                fieldData,
                this.getArticleFieldsFormArray().value
            );
            if (this.fieldVisibilityMapping[fieldData._id] === false) {
                // field is not visible, remove value if it is set, because we dont want invisible fields which have a value which could trigger other visibility conditions by mistake
                this.utils.resetFieldValueOfFieldFormGroup(fieldFormGroup);
            } else {
                // field is visible, check cases where we would auto fill a value
                if (fieldData.type === FieldType.Select && fieldData.required && fieldData.possibleValues.length === 1) {
                    // if it is a select field we could auto-fill if we only have one option to choose from
                    if (
                        fieldData.value == null ||
                        fieldData.value === '' ||
                        (Array.isArray(fieldData.value) && fieldData.value.length === 0)
                    ) {
                        // only set field.value if it currently is not set
                        if (fieldData.multiSelect) {
                            fieldFormGroup.get('value').setValue([fieldData.possibleValues[0].value]);
                        } else {
                            fieldFormGroup.get('value').setValue(fieldData.possibleValues[0].value);
                        }
                    }
                } else if (fieldData.autoFillLastValue === true && previousVisibility !== true) {
                    try {
                        // autofill is activated and field was not visible before
                        if (
                            fieldData.value == null ||
                            fieldData.value === '' ||
                            (Array.isArray(fieldData.value) && fieldData.value.length === 0)
                        ) {
                            // only set field.value if it currently is not set
                            // get autofill fields and check if we have autofill data for our current field name
                            this.storage.get('dp_autofill_fields').then((savedAutoFillFieldData) => {
                                if (Array.isArray(savedAutoFillFieldData)) {
                                    let foundAutoFillField = savedAutoFillFieldData.find((x) => x.fieldName === fieldData.name);
                                    if (foundAutoFillField != null) {
                                        if (fieldData.type === FieldType.Select) {
                                            // if the field type is select we need to check if the value we want to fill is included in possibleValues
                                            let found = false;
                                            for (let entry of fieldData.possibleValues) {
                                                if (entry.value === foundAutoFillField.fieldValue) {
                                                    found = true;
                                                    break;
                                                }
                                            }
                                            if (found) {
                                                fieldFormGroup.get('value').setValue(foundAutoFillField.fieldValue);
                                            }
                                        } else {
                                            // default handling - just set value
                                            fieldFormGroup.get('value').setValue(foundAutoFillField.fieldValue);
                                        }
                                    }
                                }
                            });
                        }
                    } catch (visibilityChangeAutoFillError) {
                        this.logger.error(
                            '[JOB ARTICLE] could not auto-fill field after changing visibility condition',
                            visibilityChangeAutoFillError
                        );
                    }
                }
            }
        }
    }

    /**
     * Triggered when the user adds an photo by selecting an attachment
     */
    onAddPhoto(photo: Attachment | Attachment[], photoCategoryName: string) {
        this.logger.log('[JOB ARTICLE PHOTOS] added job article photo', photo);

        // photo limit
        if (this.getArticlePhotosFormArray().length >= 50) {
            this.notifySrv.warn(
                this.translate.instant('projects.jobs.details.article.photo-limit-desc'),
                this.translate.instant('projects.jobs.details.article.photo-limit-title'),
                false,
                10000
            );
            return;
        }

        if (Array.isArray(photo)) {
            for (let ph of photo) {
                let photoModel = new JobPhoto(ph);
                photoModel.photoCategoryName = photoCategoryName;
                this.getArticlePhotosFormArray().push(this.utils.objectToFormGroup(photoModel));
            }
        } else {
            let photoModel = new JobPhoto(photo);
            photoModel.photoCategoryName = photoCategoryName;
            this.getArticlePhotosFormArray().push(this.utils.objectToFormGroup(photoModel));
        }

        // FIXME: dirty workaround to save job with new data on taking photos
        this.handleArticleChangeEvents(false);
        setTimeout(() => {
            this.jobSaveRequested.emit();
        }, 1000);
    }

    /**
     * Triggered when the user deletes an photo by deleting an attachment
     * @param photo
     */
    onRemovePhoto(photo: Attachment) {
        this.logger.log('[JOB ARTICLE PHOTOS] removed job article photo', photo);
        let photoIndex = this.getArticlePhotosFormArray().value.findIndex((x) => x._id === photo._id);
        if (photoIndex > -1) {
            this.getArticlePhotosFormArray().removeAt(photoIndex);
        }

        // only emit event if public view is enabled
        if (this.publicView) {
            this.notUploadedPhotosDeleted.emit(photo);
        }

        // FIXME: dirty workaround to save job with new data on taking photos
        this.handleArticleChangeEvents(false);
        setTimeout(() => {
            this.jobSaveRequested.emit();
        }, 1000);
    }

    /**
     * If a photo notice changes we need to mark our formgroup as dirty so the job extension will be saved on job save
     */
    onPhotoNoticeChanged(photoFormGroup: AbstractControl, newAttachmentData: Attachment) {
        photoFormGroup.get('notice').setValue(newAttachmentData.notice);
        this.articleFormGroup.markAsDirty();
    }

    onPhotoUpdated(photoFormGroup: AbstractControl, newAttachmentData: Attachment) {
        this.logger.log('[JOB ARTICLE PHOTOS] photo has been updated', newAttachmentData._id);
        // create new data structure and update our form group
        let newData = new JobPhoto(newAttachmentData);
        newData.photoCategoryName = (photoFormGroup.value as JobPhoto).photoCategoryName;
        photoFormGroup.patchValue(newData);
        // photoFormGroup = this.utils.objectToFormGroup(newData);
        photoFormGroup.markAsDirty();
        this.articleFormGroup.markAsDirty();

        // FIXME: dirty workaround to save job with new data on taking photos
        this.handleArticleChangeEvents(false);
        setTimeout(() => {
            this.jobSaveRequested.emit();
        }, 1000);
    }

    /**
     * Helper function which returns the fields for given article formGroup
     */
    getArticleFieldsFormArray() {
        return this.articleFormGroup.get('fields') as UntypedFormArray;
    }

    /**
     * Helper function which returns the number of visible article fields
     */
    getVisibleArticleFieldsFormArray() {
        return this.getArticleFieldsFormArray().controls.filter((x) => this.fieldVisibilityMapping[x.get('_id').value]).length;
    }

    /**
     * Helper function wich returns the photos for current article
     */
    getArticlePhotos() {
        return this.articleFormGroup.get('photos').value as Array<JobPhoto>;
    }

    /**
     * Helper function wich returns the photos for current article formGroup
     */
    getArticlePhotosFormArray() {
        return this.articleFormGroup.get('photos') as UntypedFormArray;
    }

    /**
     * Helper function wich returns the photoCategories for given article formGroup
     */
    getArticlePhotoCategoriesFormArray() {
        return this.articleFormGroup.get('photoCategories') as UntypedFormArray;
    }

    /**
     * Get amount of photos by category name
     * @param category the given categoryName
     */
    getAmountOfArticlePhotosByCategory(category: string) {
        return (
            this.getArticlePhotosFormArray()
                .getRawValue()
                .filter((x) => x.photoCategoryName === category)?.length || 0
        );
    }

    /**
     * Function to duplicate group field
     * @param idOfGroupField the id of the group field inside form array
     */
    onDuplicateGroup(idOfGroupField: string) {
        let foundArticleIndex = this.getArticleFieldsFormArray().controls.findIndex((x) => x.get('_id').value === idOfGroupField);
        if (foundArticleIndex > -1) {
            // clone article data
            let foundArticleData = this.utils.clone(this.getArticleFieldsFormArray().at(foundArticleIndex).value);
            let allDuplicatedFields = this.getArticleFieldsFormArray().controls.filter((x) =>
                x.get('_id').value.startsWith(foundArticleData._id.split('|')[0])
            );

            // get last duplication Number
            let lastDuplicationNumber = allDuplicatedFields[allDuplicatedFields.length - 1].get('duplicationNumber').value;
            if (lastDuplicationNumber == null) {
                lastDuplicationNumber = 1;
            }

            foundArticleData.duplicationNumber = lastDuplicationNumber + 1;
            foundArticleData._id = `${foundArticleData._id.split('|')[0]}|${foundArticleData.duplicationNumber}`;
            foundArticleData.repetition = false;

            // create new form group and reset fields
            let foundArticle = this.utils.objectToFormGroup(foundArticleData);
            this.utils.resetFieldValueOfFieldFormGroup(foundArticle);

            // insert new article to form array
            let indexOfLastDuplicatedField = this.getArticleFieldsFormArray().controls.findIndex(
                (x) => x.get('_id').value === allDuplicatedFields[allDuplicatedFields.length - 1].get('_id').value
            );
            if (indexOfLastDuplicatedField > -1) {
                this.getArticleFieldsFormArray().insert(indexOfLastDuplicatedField + 1, foundArticle);
                this.handleArticleChangeEvents(false);
            } else {
                console.error('error while duplicate group. Index to push: ', indexOfLastDuplicatedField);
            }
        }
    }

    /**
     * Function to delete duplicated group field
     * @param idOfGroupField the id of the group field inside form array
     */
    onDeleteDuplicatedGroup(idOfGroupField: string) {
        let foundArticleIndex = this.getArticleFieldsFormArray().controls.findIndex((x) => x.get('_id').value === idOfGroupField);
        if (foundArticleIndex > -1) {
            this.getArticleFieldsFormArray().removeAt(foundArticleIndex);
            this.handleArticleChangeEvents(false);
        }
    }

    /**
     * Function called if no-uploaded files changed
     * @param files the files to upload for later
     * @param categoryName the name of the photo category
     */
    noUploadedFilesChanged(files: File | File[], categoryName: string) {
        this.notUploadedPhotosChanged.emit({ files: files, category: categoryName });
    }

    /**
     * Get public image based on displayName and category
     * @param id the unique id of the file
     * @param categoryName the category name
     */
    getPublicImage(id: string, categoryName: string) {
        if (!this.publicView) {
            return null;
        }

        // continue because public view is enabled
        let files = this.publicPhotosToUpload.find((x) => x.category === categoryName)?.files || [];
        return files.find((x) => x.id === id)?.data;
    }

    /**
     * Checks if a field matches the current search term
     */
    isFieldMatchingSearch(fieldControl: AbstractControl): boolean {
        if (!this.articleSearchBarCurrentValue) {
            return true;
        }
        const searchTerm = this.articleSearchBarCurrentValue.toLowerCase();
        const fieldName = (fieldControl.get('name').value || '').toLowerCase();
        const fieldDesc = (fieldControl.get('description').value || '').toLowerCase();
        return fieldName.includes(searchTerm) || fieldDesc.includes(searchTerm);
    }

    /**
     * Checks if any field matches the current search term
     */
    hasSearchResults(): boolean {
        if (!this.articleSearchBarCurrentValue) {
            return true;
        }
        return this.getArticleFieldsFormArray().controls.some(
            (field) => this.fieldVisibilityMapping[field.get('_id').value] && this.isFieldMatchingSearch(field)
        );
    }

    resetArticleSearch() {
        this.articleSearchBarCurrentValue = '';
    }

    /**
     * Handle photo drop from CDK drag-and-drop.
     */
    onDropPhoto(event: CdkDragDrop<any>, targetCategoryName: string) {
        const { photo, sourceArticleFormGroup } = event.item.data;

        // Check if file type is allowed
        if (event.previousContainer !== event.container) {
            const targetCategory = this.getArticlePhotoCategoriesFormArray().value.find((c) => c.categoryName === targetCategoryName);
            if (targetCategory && !this.attachmentSrv.isFileTypeAllowed(photo.fileMimeType, targetCategory.fileType)) {
                this.notifySrv.warn(this.translate.instant('core.attachment.errors.file-type-not-allowed-err'));
                return;
            }
        }

        try {
            if (event.previousContainer === event.container) {
                // reorder within category
                this.logger.log('[JOB ARTICLE PHOTOS] Reorder within category');
                if (event.previousIndex === event.currentIndex) return;
                const formArray = this.getArticlePhotosFormArray();
                const categoryControls = formArray.controls.flatMap((ctrl, index) =>
                    ctrl.value.photoCategoryName === targetCategoryName ? [index] : []
                );

                const from = categoryControls[event.previousIndex];
                const to = categoryControls[event.currentIndex];
                const ctrl = formArray.at(from);
                formArray.removeAt(from);
                formArray.insert(to, ctrl);
                this.articleFormGroup.markAsDirty();
                return;
            } else {
                // move to another category or component
                this.logger.log('[JOB ARTICLE PHOTOS] Move attachment to other category or component');
                // Remove from source
                if (sourceArticleFormGroup) {
                    const sourcePhotos = sourceArticleFormGroup.get('photos') as UntypedFormArray;
                    const index = sourcePhotos.controls.findIndex((ctrl) => ctrl.value._id === photo._id);
                    if (index > -1) {
                        sourcePhotos.removeAt(index);
                        sourceArticleFormGroup.markAsDirty();
                    }
                } else {
                    const index = this.job.photos.findIndex((p) => p._id === photo._id);
                    if (index > -1) this.job.photos.splice(index, 1);
                }

                // Add to the new article
                const formArray = this.getArticlePhotosFormArray();
                const newPhoto = new JobPhoto({ ...photo, photoCategoryName: targetCategoryName });
                const targetIndexes = formArray.controls.flatMap((c, i) => (c.value.photoCategoryName === targetCategoryName ? [i] : []));
                const insertAt = targetIndexes[event.currentIndex] ?? formArray.length;
                formArray.insert(insertAt, this.utils.objectToFormGroup(newPhoto));

                this.articleFormGroup.markAsDirty();
                this.handleArticleChangeEvents(false);
                setTimeout(() => this.jobSaveRequested.emit(), 1000);
            }
        } catch (error) {
            this.logger.error('[JOB ARTICLE PHOTOS] Error moving attachment to other category or component', error);
            this.notifySrv.error(this.translate.instant('core.attachment.errors.move-attachment-err'));
        }
    }

    onDragEntered(event: CdkDragEnter<any>) {
        const photo = event.item.data?.photo;
        const targetFileType = event.container.data?.fileType;
        if (photo && targetFileType) {
            this.dragTargetAllowed = this.attachmentSrv.isFileTypeAllowed(photo.fileMimeType, targetFileType);
        } else {
            this.dragTargetAllowed = true;
        }
    }

    /**
     *  Native HTML5 drag & drop support (used to drag photos between different browser windows).
     * @param event
     * @param photo
     * @param sourceCategoryName
     */

    onPhotoDragStart(event: DragEvent, photo: JobPhoto, sourceCategoryName: string) {
        if (!this.changesAllowed || !event.dataTransfer) {
            return;
        }
        event.dataTransfer.setData(
            'application/x-dokupit-attachment',
            JSON.stringify({
                jobId: this.job._id,
                projectId: this.project._id,
                sourceCategoryName,
                photo: this.utils.clone(photo),
            })
        );
    }

    async onCategoryNativeDrop(event: DragEvent, categoryName: string) {
        const rawPhotoPayload = event.dataTransfer?.getData('application/x-dokupit-attachment');
        if (!rawPhotoPayload || !this.changesAllowed) {
            return; // not our own drag payload (e.g. a real OS file) - nothing for us to do here
        }
        event.preventDefault();
        await this.handleNativePhotoDrop(rawPhotoPayload, categoryName);
    }

    /**
     * handle actual drop of native drag payload
     * @param rawPhotoPayload
     * @param targetCategoryName
     * @private
     */
    private async handleNativePhotoDrop(rawPhotoPayload: string, targetCategoryName: string) {
        let payload: { jobId: string; projectId: string; sourceCategoryName: string; photo: JobPhoto };
        try {
            payload = JSON.parse(rawPhotoPayload);
        } catch (err) {
            this.logger.error('[JOB ARTICLE PHOTOS] Could not parse native drag payload', err);
            return;
        }

        const targetCategory = this.getArticlePhotoCategoriesFormArray().value.find((c) => c.categoryName === targetCategoryName);
        if (targetCategory && !this.attachmentSrv.isFileTypeAllowed(payload.photo.fileMimeType, targetCategory.fileType)) {
            this.notifySrv.warn(this.translate.instant('core.attachment.errors.file-type-not-allowed-err'));
            return;
        }

        if (this.getArticlePhotosFormArray().length >= 50) {
            this.notifySrv.warn(
                this.translate.instant('projects.jobs.details.article.photo-limit-desc'),
                this.translate.instant('projects.jobs.details.article.photo-limit-title'),
                false,
                10000
            );
            return;
        }

        try {
            const newPhoto: JobPhoto = this.utils.duplicateItem(payload.photo);
            newPhoto.fileName = newPhoto.fileName.replace(payload.photo._id, newPhoto._id);
            newPhoto.filePath = `${this.project?._id}/jobs/${this.job._id}/${newPhoto.fileName}`;
            newPhoto.photoCategoryName = targetCategoryName;

            await this.attachmentSrv.duplicateAttachment(payload.photo.filePath, newPhoto.filePath);

            this.getArticlePhotosFormArray().push(this.utils.objectToFormGroup(newPhoto));
            this.articleFormGroup.markAsDirty();
            this.handleArticleChangeEvents(false);
            setTimeout(() => this.jobSaveRequested.emit(), 1000);
        } catch (error) {
            this.logger.error('[JOB ARTICLE PHOTOS] Error duplicating dragged photo', error);
            this.notifySrv.error(this.translate.instant('core.attachment.errors.move-attachment-err'));
        }
    }
}
